import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, MapPin } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTAButtons } from "@/components/site/CTAButtons";
import { GuidePrices } from "@/components/site/GuidePrices";
import { TrustedLocal } from "@/components/site/TrustedLocal";
import { SITE } from "@/config/site";
import faceBgAssetUrl from "@/assets/izhub-face-bg.webp";
const faceBgAsset = { url: faceBgAssetUrl };
import hero from "@/assets/hero-clean-living-room.webp";

const SITE_URL = "https://izhubcleaning.uk";
const URL = `${SITE_URL}/domestic-cleaning-tidworth`;

const NEIGHBOURHOODS = [
  "Tidworth",
  "Tidworth Garrison",
  "Perham Down",
  "Ludgershall",
  "Shipton Bellinger",
  "Collingbourne Ducis",
  "Bulford",
  "Larkhill",
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "@id": `${URL}#service`,
      serviceType: "Domestic House Cleaning",
      name: "Domestic House Cleaning in Tidworth",
      description:
        "Weekly, fortnightly and one-off domestic house cleaning in Tidworth, Perham Down, Ludgershall and Shipton Bellinger. Fully insured local cleaners from £22 per hour.",
      url: URL,
      areaServed: NEIGHBOURHOODS.map((name) => ({ "@type": "City", name })),
      provider: {
        "@type": "LocalBusiness",
        "@id": `${SITE_URL}/#localbusiness`,
        name: "IZHUB Cleaning Service",
        telephone: ["+44 7763 933920"],
        url: SITE_URL,
        address: {
          "@type": "PostalAddress",
          addressLocality: "Tidworth",
          addressRegion: "Wiltshire",
          addressCountry: "GB",
        },
      },
      offers: {
        "@type": "Offer",
        price: "22",
        priceCurrency: "GBP",
        availability: "https://schema.org/InStock",
        priceSpecification: {
          "@type": "PriceSpecification",
          price: "22",
          priceCurrency: "GBP",
          description: "per hour",
        },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Domestic House Cleaning in Tidworth", item: URL },
      ],
    },
  ],
};

export const Route = createFileRoute("/domestic-cleaning-tidworth")({
  head: () => ({
    meta: [
      { title: "Domestic House Cleaning in Tidworth | IZHUB Cleaning" },
      {
        name: "description",
        content:
          "Domestic house cleaning in Tidworth from £22 per hour. Weekly, fortnightly and one-off cleans across Tidworth, Perham Down, Ludgershall and Shipton Bellinger. Fully insured local cleaners.",
      },
      { property: "og:title", content: "Domestic House Cleaning in Tidworth | IZHUB Cleaning" },
      {
        property: "og:description",
        content:
          "Trusted, insured house cleaners in Tidworth. Guide prices from £22 per hour — get an instant online quote in under a minute.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
  }),
  component: DomesticTidworth,
});

function DomesticTidworth() {
  return (
    <>
      <PageHero
        title={<>Domestic House Cleaning in <span className="rainbow-text">Tidworth</span></>}
        intro="Reliable weekly, fortnightly and one-off house cleaning for homes across Tidworth, Perham Down, Ludgershall and Shipton Bellinger. Fully insured, locally run, and easy to book."
        withBackground
        backgroundImage={faceBgAsset.url}
      >
        <CTAButtons />
      </PageHero>

      <section className="border-y border-border bg-background/85 backdrop-blur-sm">
        <div className="container-x py-16 md:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
          <img
            src={hero}
            alt="Freshly cleaned living room in a Tidworth home"
            loading="lazy"
            className="rounded-3xl w-full h-auto object-cover aspect-[4/3] shadow-soft"
          />
          <div>
            <h2 className="font-display text-3xl md:text-4xl">What a Tidworth house clean includes</h2>
            <ul className="mt-6 space-y-2.5">
              {[
                "Kitchens — worktops, sinks, hob, appliance exteriors, floors",
                "Bathrooms — sanitising, descaling, mirrors, tiles, floors",
                "Bedrooms and living areas — dusting, vacuuming, mopping",
                "Skirting boards, switches and other commonly-missed spots",
                "Same cleaner each visit wherever possible",
                "All products and equipment provided as standard",
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote" search={{ service: "regular" }} className="btn-primary">Get my instant quote</Link>
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-outline">WhatsApp Us</a>
            </div>
          </div>
        </div>
      </section>

      <GuidePrices />

      <section className="py-16 md:py-24 bg-background/85 backdrop-blur-sm">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl">House cleaning across the Tidworth area</h2>
          <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
            <p>
              IZHUB Cleaning Services provides regular domestic cleaning to households throughout Tidworth and the villages
              around it. Whether you need a weekly visit to keep on top of a busy family home, a fortnightly clean while you
              work, or a one-off tidy before guests arrive, we build the visit around your priorities.
            </p>
            <p>
              We work across Tidworth town, Tidworth Garrison, Perham Down, Ludgershall, Shipton Bellinger, Collingbourne,
              Bulford and Larkhill. Because we are based locally rather than run from a national call centre, you get the same
              cleaner, a direct phone number and a reply within about two hours during business hours.
            </p>
            <p>
              Every cleaner is ID-checked, trained and covered by public liability insurance. Prices start from £22 per hour
              with products and equipment included, and there are no call-out charges or hidden fees.
            </p>
          </div>

          <h3 className="mt-10 font-display text-2xl">Areas we cover</h3>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {NEIGHBOURHOODS.map((a) => (
              <span key={a} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
                <MapPin className="h-3.5 w-3.5 text-primary" />
                {a}
              </span>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Somewhere else nearby? <Link to="/service-areas" className="text-primary underline underline-offset-4">See all service areas</Link>.
          </p>
        </div>
      </section>

      <TrustedLocal />

      <section className="pb-24">
        <div className="container-x">
          <div className="rounded-3xl bg-sage-soft/60 border border-sage/30 p-8 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl max-w-2xl mx-auto">Book a house cleaner in Tidworth</h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Get an instant price online, or message us on WhatsApp — we usually reply within 2 hours.
            </p>
            <div className="mt-8 flex justify-center"><CTAButtons /></div>
          </div>
        </div>
      </section>
    </>
  );
}
